import React, { useEffect } from "react";
import { faEdit, faTimes } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

interface CardComponentProps {
  selectedLocation: any;
  isEditing: boolean;
  editedUsername: string;
  handleSaveEdit: () => void;
  setIsEditing: React.Dispatch<React.SetStateAction<boolean>>;
  setEditedUsername: React.Dispatch<React.SetStateAction<string>>;
  loginTime: string;
}

const InfoRow = ({
  label,
  value,
  valueClassName = "",
}: {
  label: string;
  value: React.ReactNode;
  valueClassName?: string;
}) => (
  <div className="d-flex flex-column flex-md-row gap-2">
    <span className="fw-bold" style={{ minWidth: "120px" }}>
      {label}
    </span>
    <span className={`text-muted ${valueClassName}`}>{value}</span>
  </div>
);

const InfoRowEditable = ({ label, children }) => (
  <div className="d-flex flex-column flex-md-row gap-2 align-items-start">
    <span className="fw-bold" style={{ minWidth: "120px" }}>
      {label}
    </span>
    <div className="flex-grow-1">{children}</div>
  </div>
);

const parseAsUTC = (iso: string) => {
  const s = String(iso);
  if (/[zZ]$/.test(s) || /[+-]\d{2}:\d{2}$/.test(s)) return new Date(s);
  return new Date(s + "Z");
};

const formatUtcLabel = (raw: number | string) => {
  const n = Number(raw) || 0; // mis. 7, 7.5, -3, -3.5
  const sign = n >= 0 ? "+" : "-";
  const abs = Math.abs(n);
  const hours = Math.trunc(abs);
  const minutes = Math.round((abs - hours) * 60);
  const hh = String(hours).padStart(2, "0");
  const mm = String(minutes).padStart(2, "0");
  return `UTC${sign}${hh}:${mm}`;
};

const computeTimePlusUtc = (timeIso: string, utcOffset: number | string) => {
  const baseUtc = parseAsUTC(timeIso);
  const n = Number(utcOffset) || 0;
  const shifted = new Date(baseUtc.getTime() + n * 3600_000);

  const display_enUS = shifted.toLocaleString("en-US", {
    dateStyle: "medium",
    timeStyle: "medium",
    timeZone: "UTC",
  });
  const display_idID = shifted.toLocaleString("id-ID", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    timeZone: "UTC",
  });

  return {
    display_enUS,
    display_idID,
    iso: shifted.toISOString(),
    utcLabel: formatUtcLabel(utcOffset),
  };
};

const CardComponent: React.FC<CardComponentProps> = ({
  selectedLocation,
  isEditing,
  editedUsername,
  handleSaveEdit,
  setIsEditing,
  setEditedUsername,
  loginTime,
}) => {
  const timePlusUtc =
    selectedLocation?.time != null && selectedLocation?.utc != null
      ? computeTimePlusUtc(selectedLocation.time, selectedLocation.utc)
      : null;

  // logging yang jelas
  useEffect(() => {
    if (!selectedLocation) return;
    // console.group("Selected Location (time + utc)");
    // console.log("Time (raw UTC):", selectedLocation.time);
    // console.log("UTC offset:", selectedLocation.utc);
    if (timePlusUtc) {
      // console.log("Result (id-ID):", timePlusUtc.display_idID);
      // console.log("Result (en-US):", timePlusUtc.display_enUS);
      // console.log("Result ISO:", timePlusUtc.iso);
    }
    console.groupEnd();
  }, [selectedLocation, timePlusUtc]);

  return (
    <div
      className="container"
      style={{
        maxWidth: "600px",
        width: "90%",
        padding: "20px",
        boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
        borderRadius: "8px",
        backgroundColor: "white",
        marginLeft: "20px",
      }}
    >
      {/* <h5>Device Information</h5> */}
      <h5>{selectedLocation.username}</h5>
      <hr />
      <div className="d-flex flex-column gap-3">
        <InfoRow label="Device ID" value={selectedLocation.id_device} />

        <InfoRowEditable label="User">
          {isEditing ? (
            <div className="d-flex flex-column flex-md-row gap-2 w-100">
              {/* Visually hidden label, referenced by input */}
              <label
                id="username-edit-label"
                htmlFor="username-edit-input"
                className="visually-hidden"
              >
                User
              </label>

              <input
                id="username-edit-input"
                type="text"
                className="form-control form-control-sm"
                value={editedUsername}
                onChange={(e) => setEditedUsername(e.target.value)}
                onBlur={(e) => {
                  if (
                    (e.relatedTarget as HTMLElement)?.dataset?.cancelBtn ===
                    "true"
                  )
                    return;
                  handleSaveEdit();
                }}
                aria-labelledby="username-edit-label"
                placeholder="Enter username"
                autoFocus
              />

              <button
                type="button"
                className="btn btn-secondary btn-sm"
                // onMouseDown fires before input blur → mark as cancel so onBlur can skip save
                onMouseDown={(e) =>
                  (e.currentTarget.dataset.cancelBtn = "true")
                }
                onClick={() => setIsEditing(false)}
                title="Cancel"
                data-cancel-btn="true"
              >
                <FontAwesomeIcon icon={faTimes} aria-hidden="true" />
                <span className="visually-hidden">Cancel edit</span>
              </button>
            </div>
          ) : (
            <button
              type="button"
              className="btn btn-link p-0 d-inline-flex align-items-center text-decoration-none text-secondary"
              onClick={() => {
                setEditedUsername(selectedLocation.username);
                setIsEditing(true);
              }}
              title="Edit user"
            >
              <span>{selectedLocation.username}</span>
              <FontAwesomeIcon
                icon={faEdit}
                className="ms-2 text-secondary"
                aria-hidden="true"
              />
              <span className="visually-hidden">Edit user</span>
            </button>
          )}
        </InfoRowEditable>

        <InfoRow
          label="Battery"
          value={`${selectedLocation.battery ?? "0"} %`}
        />

        {/* <InfoRow
          label="Location Time"
          value={
            selectedLocation?.timeZone
              ? new Intl.DateTimeFormat("en-US", {
                  timeZone: selectedLocation.timeZone,
                  dateStyle: "medium",
                  timeStyle: "medium",
                }).format(new Date(selectedLocation.time))
              : "Loading..."
          }
        /> */}

        <InfoRow
          label="Location Time"
          value={
            timePlusUtc ? (
              <>
                {timePlusUtc.display_enUS}{" "}
                <small className="text-muted">({timePlusUtc.utcLabel})</small>
              </>
            ) : (
              "Loading..."
            )
          }
        />

        <InfoRow
          label="User Time"
          value={
            loginTime
              ? new Intl.DateTimeFormat("en-US", {
                  dateStyle: "medium",
                  timeStyle: "medium",
                }).format(new Date(Number(loginTime)))
              : "Loading..."
          }
        />
      </div>

      {isEditing && (
        <button
          className="btn btn-secondary mt-4"
          onClick={() => setIsEditing(false)}
        >
          Close
        </button>
      )}
    </div>
  );
};

export default CardComponent;
